// utils
import { parseBackendDateTime } from 'utils/date';

// types
import type { RegisterAbsenceWorkAgreement } from 'RestAPIs/roster/register-absence';

export const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const;

export type Weekday = (typeof WEEKDAYS)[number];

/** Per work agreement: how new (sickness) duties are rolled out when the
 *  employee has no planned duties — the planner's decision. The rollout is an
 *  explicit opt-in; by default only the planned duties are converted. */
export type WorkAgreementSetting = {
  useFteRollout: boolean;
  /** the planner (un)checked the rollout: stop choosing for them */
  useFteRolloutTouched?: boolean;
  rolloutStartDate: Date | null;
  /** the planner set the rollout date by hand: stop following the period */
  rolloutStartDateTouched?: boolean;
  inputDays: Weekday[];
  inputWeekHours: number;
};

/** A zero-hours contract (NUC) carries no week hours of its own: the planner
 *  enters them per absence, so nothing about it can be filled in up front. */
export const isZeroHoursContract = (
  workAgreement: RegisterAbsenceWorkAgreement
) => workAgreement.contractCode === '0';

/** PNIL ('tmp') is paid through the external organisation: the absence itself
 *  can be registered (the roster must be emptied), but no sickness duties are
 *  ever created, so the rollout stays off. The backend refuses it as well. */
export const isPnilContract = (workAgreement: RegisterAbsenceWorkAgreement) =>
  workAgreement.contractCode === 'tmp';

/** Defaults: rollout is off; when the planner opts in, duties are created from
 *  the first day of the absence, so the whole period is covered. The
 *  checkboxes are the recorded working days, else the contract day flags. */
export const createDefaultSetting = (
  workAgreement: RegisterAbsenceWorkAgreement,
  fallbackRolloutStartDate: Date | null,
  recorded?: { days: Weekday[] }
): WorkAgreementSetting => ({
  useFteRollout: false,
  rolloutStartDate:
    fallbackRolloutStartDate ??
    (workAgreement.fteStartDate
      ? parseBackendDateTime(workAgreement.fteStartDate)
      : null),
  // the planner's recorded working days win over the (often empty) contract
  // day flags
  inputDays:
    recorded?.days ??
    WEEKDAYS.filter((day) => workAgreement[`${day}Hours`] === true),
  // deliberately NOT the recorded hours: on a zero-hours contract the hours
  // differ per absence, so carrying the previous ones over would quietly book
  // the wrong number. Only the days are worth remembering; the hours come
  // from the work agreement (zero on a zero-hours contract) and are entered
  // per sickness notice.
  inputWeekHours: workAgreement.hoursPerWeek ?? workAgreement.weekHoursMin ?? 0
});
